import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { useScrollToElement } from '@/hooks/useScrollToElement'
import { useToggle } from '@/hooks/useToggle'
import { useTranslate } from '@/utils/translate/translate-client'
import {
  GButtonSM,
  GEyebrowLG,
  GEyebrowMD,
  GParagraphLG,
  GParagraphMD,
  GResponsiveText,
  GTitleLG,
  GTitleSM,
  GTitleXL,
  GTitleXS,
} from '../Typography'

interface VacationType {
  mobileImage: string
  desktopImage: string
  title: string
  mobileTitle: string
  price: string
  description: string
  items: {
    image: string
    title: string
    info: string[]
  }[]
}

const vacationInfo: VacationType[] = [
  {
    mobileImage: 'https://images.angelstudios.com/image/upload/v1738860908/Grand_Canyon_3_jbnzkg.png',
    desktopImage: 'https://images.angelstudios.com/image/upload/v1738860909/Grand_Canyon_Wide_3_nmow75.png',
    title: 'Grand Canyon & Sedona: A Luxurious Southwest Escape',
    mobileTitle: 'Jungle Safari Expedition',
    price: '$100,000',
    description:
      'Discover the breathtaking beauty of the American Southwest with this all-inclusive luxury adventure. Fly First Class with three companions to Phoenix, where a private limousine awaits. Embark on a seven-day journey through Scottsdale, Sedona, and the Grand Canyon, staying in world-class accommodations and exploring stunning landscapes. Soar over the Grand Canyon on an exhilarating helicopter tour and enjoy $5,000 in spending cash for unforgettable experiences. This is the ultimate way to explore Arizona’s natural wonders in style.',
    items: [
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861096/first-class-seats-01_rsl4mo.jpg',
        title: 'First-Class Airfare for Four',
        info: ['Round-trip First Class flights for four travelers.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/luxury-car-service_uybran.jpg',
        title: 'Luxury Ground Transportation',
        info: ['Private limousine service to and from your home.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861098/grand-canyon-walls-01_chgnvr.jpg',
        title: '7-Day Grand Canyon & Sedona Experience',
        info: [
          'Epic road trip through Phoenix, Scottsdale, Grand Canyon South Rim, and Sedona.',
          'Luxury car rental from Phoenix International Airport during your stay.',
          'Exclusive guided tours of breathtaking national parks and iconic landmarks.',
          'Luxury accommodations at top-rated resorts throughout the journey.',
          'Private experiences tailored for adventure and relaxation.',
        ],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861096/helicopter-tour-01_ys5bn8.jpg',
        title: 'Grand Canyon Helicopter Tour',
        info: [
          "Breathtaking aerial views of the Grand Canyon's South Rim.",
          "25–30-minute scenic flight over some of the canyon's most famous rock formations.",
          'Unmatched photo opportunities from a state-of-the-art helicopter.',
        ],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/money-american-dollars-cash-01_mpum8h.jpg',
        title: '$5,000 Spending Cash',
        info: ['Use for dining, souvenirs, spa treatments, or additional excursions.'],
      },
    ],
  },
  {
    mobileImage: 'https://images.angelstudios.com/image/upload/v1738860909/Jungle_Safari_3_uvjatg.png',
    desktopImage: 'https://images.angelstudios.com/image/upload/v1738860908/Jungle_Safari_Wide_3_ogenod.png',
    title: 'Tasmania Jungle Safari & Luxury Wilderness Retreat',
    mobileTitle: 'Jungle Safari Expedition',
    price: '$100,000',
    description:
      'Escape to the wild beauty of Tasmania on a seven-day luxury jungle safari. Fly First Class to this remote paradise, where a private limousine awaits to whisk you away. Journey through lush rainforests, rugged coastlines, and pristine wilderness while enjoying five-star accommodations. Encounter exotic wildlife, explore breathtaking landscapes, and indulge in gourmet cuisine. Plus, enjoy $5,000 in spending cash to elevate your adventure. This is the ultimate blend of luxury and untamed exploration.',
    items: [
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/first-class-seat-02_ipiesj.jpg',
        title: 'First-Class Airfare for Two',
        info: ['Round-trip First Class flights from the U.S. to Tasmania, Australia.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/limo-service-01_llkew8.jpg',
        title: 'Luxury Ground Transportation',
        info: ['Private limousine service from your home to the airport and back upon return.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861097/tasmania-australia-01_tdtivt.jpg',
        title: '7-Day Luxury Jungle Safari & Wilderness Excursion',
        info: [
          "Stay in Tasmania's finest five-star accommodations, surrounded by nature.",
          'Explore dense rainforests, rugged coastlines, and breathtaking national parks.',
          'Exclusive guided wildlife safaris featuring encounters with Tasmanian devils, wombats, and rare birds.',
          'Private tours through Cradle Mountain-Lake St Clair National Park and Freycinet National Park.',
          'Scenic boat tours to explore hidden coves, towering sea cliffs, and pristine beaches.',
          "Gourmet dining experiences featuring Tasmania's world-renowned farm-to-table cuisine.",
          'Opportunities for adventure activities such as kayaking, hiking, and wildlife spotting.',
        ],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/money-american-dollars-cash-01_mpum8h.jpg',
        title: '$5,000 Spending Cash',
        info: ['Use for dining, souvenirs, spa treatments, or additional excursions.'],
      },
    ],
  },
  {
    mobileImage: 'https://images.angelstudios.com/image/upload/v1738860908/Private_Island_3_v8cwx5.png',
    desktopImage: 'https://images.angelstudios.com/image/upload/v1738860908/Private_Island_Wide_3_hdwy4z.png',
    title: 'Luxury Private Island Getaway',
    mobileTitle: 'Luxury Private Island Getaway',
    price: '$100,000',
    description:
      'Immerse yourself in paradise with a seven-day luxury retreat to Tetiaroa, French Polynesia. Fly First Class to this breathtaking private island, once a retreat for Tahitian royalty. Stay at The Brando, a world-renowned eco-luxury resort offering unmatched exclusivity and serenity. Indulge in pristine beaches, crystal-clear lagoons, and five-star dining, all while being surrounded by untouched natural beauty. With a private limousine to and from the airport and $5,000 in spending cash, this is your chance to experience tropical luxury at its finest.',
    items: [
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/first-class-seat-03_woksuv.jpg',
        title: 'First-Class Airfare for Two',
        info: ['Round-trip First Class flights from the U.S. to Tetiaroa, French Polynesia.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/luxury-car-service_uybran.jpg',
        title: 'Luxury Ground Transportation',
        info: ['Private limousine service from your home to the airport and back upon return.'],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861096/french-polynesia-brando-island-01_rafyac.webp',
        title: '7-Day Luxury Escape at The Brando',
        info: [
          'Stay in a lavish private villa with a private pool and direct beach access.',
          'All-inclusive gourmet dining featuring Polynesian and French cuisine.',
          'Exclusive access to untouched beaches, coral reefs, and tropical gardens.',
          "Private guided snorkeling, paddleboarding, and eco-tours through the atoll's pristine waters.",
          'Spa treatments at Varua Te Ora Polynesian Spa, inspired by ancient healing traditions.',
          'Cultural experiences, including Polynesian dance, music, and history tours.',
          'Personalized excursions such as deep-sea fishing, kayaking, and wildlife encounters.',
        ],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/money-american-dollars-cash-01_mpum8h.jpg',
        title: '$5,000 Spending Cash',
        info: ['Use for additional activities, spa treatments, fine dining, or unique island experiences.'],
      },
    ],
  },
  {
    mobileImage: 'https://images.angelstudios.com/image/upload/v1738860908/Your_Own_Vacation_3_qkiveh.png',
    desktopImage: 'https://images.angelstudios.com/image/upload/v1738860908/Your_Own_Vacation_Wide_3_nbjcv4.png',
    title: 'Choose Your Own Dream Vacation',
    mobileTitle: 'Choose Your Own Dream Vacation',
    price: '$100,000',
    description:
      'Design the ultimate getaway with $50,000 in travel credit through our premier travel agency. Whether you dream of an African safari, a private yacht tour through the Mediterranean, or a luxurious escape to the Maldives, our travel experts will craft a once-in-a-lifetime experience tailored just for you. Plus, you’ll receive $50,000 in Bitcoin—use it as an investment, enhance your trip, or cover taxes on your unforgettable adventure. The world is yours to explore, exactly the way you want.',
    items: [
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861095/vacation-passport-map-image-01_axkbou.jpg',
        title: '$50,000 in Travel Credit',
        info: [
          'Plan a completely customized vacation with our partnered luxury travel agency.',
          "Choose from the world's most breathtaking destinations and exclusive experiences.",
          'Enjoy five-star accommodations, private tours, and VIP perks tailored to your dream trip.',
        ],
      },
      {
        image: 'https://images.angelstudios.com/image/upload/v1738861094/bitcoin-01_en8yut.jpg',
        title: '$50,000 in Bitcoin',
        info: [
          'Use as an investment, for additional luxury upgrades, or to help cover taxes.',
          'Flexible to enhance your dream vacation however you see fit.',
        ],
      },
    ],
  },
]

export const VacationTab = () => {
  const { t } = useTranslate('tickets')

  return (
    <div className="w-full px-4 pb-[80px] md:pb-[120px]">
      <div className="my-10 md:mt-16">
        <GResponsiveText color="giveaway-primary" MobileTextComponent={GTitleLG} DesktopTextComponent={GTitleXL}>
          {t('vacationPackageOptions', 'Vacation Package Options')}
        </GResponsiveText>

        <GParagraphLG color="giveaway-subtle" className="mt-3">
          {t(
            'prizeItemsDescriptionV2',
            'Discover our exclusive collection of curated dream vacations offering unparalleled luxury and unique experiences, or create your own bespoke $100,000 getaway with our premier partnered travel agency—the world is yours to explore in ultimate style and comfort.',
          )}
        </GParagraphLG>
      </div>

      <div className="grid gap-8 md:gap-10">
        {vacationInfo.map((vacation) => (
          <VacationItem key={vacation.title} {...vacation} />
        ))}
      </div>
    </div>
  )
}

const VacationItem = ({ title, mobileTitle, desktopImage, mobileImage, price, description, items }: VacationType) => {
  const { t } = useTranslate('tickets')
  const [isOpen, toggleOpen] = useToggle(false)
  const { ref, scrollToElement } = useScrollToElement<HTMLDivElement>()

  return (
    <div className="flex flex-col py-4">
      <div className="aspect-h-9 aspect-w-16 relative !min-h-[230px] overflow-hidden rounded-lg md:!aspect-h-1 md:!aspect-w-[2.387]">
        <Image alt="vacation" src={mobileImage} fill className="object-cover object-bottom md:hidden" />
        <Image alt="vacation" src={desktopImage} fill className="hidden object-contain object-bottom md:block" />
      </div>

      <div className="mt-6 flex items-center justify-between md:mt-4">
        <div className="flex flex-col gap-1">
          <GResponsiveText
            color="giveaway-text-subtle-light"
            MobileTextComponent={GEyebrowMD}
            DesktopTextComponent={GEyebrowLG}
          >
            {t('giveawayItem', 'item')}
          </GResponsiveText>
          <GTitleXS color="giveaway-text-primary-light" className="md:hidden">
            {mobileTitle}
          </GTitleXS>
          <GTitleSM color="giveaway-text-primary-light" className="hidden md:block">
            {title}
          </GTitleSM>
        </div>

        <div className="flex flex-col items-end gap-1">
          <GResponsiveText
            color="giveaway-text-subtle-light"
            MobileTextComponent={GEyebrowMD}
            DesktopTextComponent={GEyebrowLG}
          >
            {t('giveawayTotalValue', 'total value')}
          </GResponsiveText>
          <GResponsiveText
            MobileTextComponent={GTitleXS}
            DesktopTextComponent={GTitleSM}
            color="giveaway-text-primary-light"
          >
            {price}
          </GResponsiveText>
        </div>
      </div>

      <GResponsiveText
        MobileTextComponent={GParagraphMD}
        DesktopTextComponent={GParagraphLG}
        color="giveaway-text-subtle-light"
        className="mt-4"
      >
        {description}
      </GResponsiveText>

      <div ref={ref}>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0, y: -20 }}
              animate={{ height: 'auto', opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -10 }}
              transition={{
                duration: 0.4,
                opacity: { duration: 0.3 },
                height: { duration: 0.4 },
                y: { duration: 0.3 },
                ease: [0.4, 0, 0.2, 1],
                staggerChildren: 0.1,
              }}
              className="mt-6 flex flex-col gap-6 overflow-hidden md:mt-4 md:gap-4"
            >
              {items.map((item, index) => (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: 'easeOut',
                  }}
                  key={title}
                  className="md:flex md:flex-row md:items-center md:gap-4"
                >
                  <div className="aspect-h-1 aspect-w-2 relative shrink-0 overflow-hidden rounded-[10px] md:aspect-h-unset md:aspect-w-unset md:h-[140px] md:w-[140px]">
                    <Image src={item.image} alt="" fill />
                  </div>

                  <div>
                    <GResponsiveText
                      MobileTextComponent={GParagraphMD}
                      DesktopTextComponent={GParagraphLG}
                      color="giveaway-text-subtle-light"
                      className="mt-2 !font-bold"
                    >
                      {title}
                    </GResponsiveText>

                    <ul className="list-inside list-disc px-4">
                      {item.info.map((itemText, index) => (
                        <li key={`${itemText}-${index}`}>
                          <GResponsiveText
                            MobileTextComponent={GParagraphMD}
                            DesktopTextComponent={GParagraphLG}
                            color="giveaway-text-subtle-light"
                            desktopClassName="hidden md:!inline"
                            mobileClassName="!inline md:hidden"
                          >
                            {itemText}
                          </GResponsiveText>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Button
        className="mt-6 flex flex-col rounded-[14px] border-[2px] !border-giveaway-primary-accent !py-[14px] hover:!bg-transparent md:mt-4 md:!px-6"
        variant="core-gray-900"
        outline
        onClick={() => {
          toggleOpen()
          scrollToElement()
        }}
      >
        <GButtonSM color="giveaway-primary-accent" className="uppercase">
          {isOpen ? t('lessDetailsGiveaway', 'Less Details') : t('moreDetailsGiveaway', 'More Details')}
        </GButtonSM>
      </Button>
    </div>
  )
}
